import { useState } from 'react'
import { useUpdateBooking } from '@/entities/booking'
import { isProblem, problemDetail, problemText } from '@/shared/api'
import type { BookingDetail, UpdateBooking } from '@/shared/api'
import { Button, Dialog, Input, Textarea } from '@/shared/ui'
import s from './BookingEditDialog.module.css'

/**
 * Предел длины примечания — доменный (`MaxNotesRunes = 5000`), в контракте стоит `maxLength` у обоих
 * полей `UpdateBooking`. ⚠️ Здесь его КОПИЯ числом: сгенерированные типы ограничения не несут —
 * `maxLength` живёт в схеме, а в TypeScript приезжает просто `string`.
 */
const NOTES_LIMIT = 5000

/**
 * «Изменить бронь» — окно правки трёх полей, которые домен у брони правит.
 *
 * ПОЧЕМУ ИМЕННО ЭТИ ТРИ: `EditBookingRequest` принимает `guests_count`, `internal_notes` и
 * `block_notes` — и всё. Даты и суммы не правятся вовсе, квартира меняется `TransferBooking`,
 * статус — действиями карточки и «вмешаться в бронь». Форма, предлагающая больше, обещала бы
 * работу, которой сервер не сделает.
 *
 * ⚠️ У ЗАКРЫТОГО ПЕРИОДА ПОЛЯ ДРУГИЕ: `block_notes` домен принимает ТОЛЬКО у блокировки и отвечает
 * отказом у обычной брони, а «сколько гостей» у закрытого периода бессмысленно — единица стоит там
 * по построению («блок логически — 1 слот»). Поэтому наборы разные, а не один с погашенными полями.
 *
 * ⚠️ ОТПРАВЛЯЕМ ТОЛЬКО ИЗМЕНЁННОЕ, потому что у примечания ТРИ состояния: пустая строка СТИРАЕТ
 * его, отсутствие поля значит «не трогать». Отправляй мы поле всегда — «Сохранить» без единой
 * правки переписывало бы примечание самим собой, а у брони без него записывало бы пустую строку.
 */
export function BookingEditDialog({
  booking,
  open,
  onOpenChange,
}: {
  booking: BookingDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const blocked = booking.status === 'blocked'
  const update = useUpdateBooking(
    booking.id,
    `${blocked ? 'Правка закрытого периода' : 'Правка брони'} ${booking.code}`,
  )

  /*
   * ⚠️ СНИМОК ТОГО, ЧТО ЧЕЛОВЕК ВИДЕЛ, КОГДА ОТКРЫВАЛ ОКНО, — включая версию: и сравнение, и
   * `If-Match` идут против НЕГО, а не против свежего пропса. `booking` перечитывается (`staleTime`
   * полминуты плюс перезапрос при возврате фокуса), и возьми мы значения из пропса в момент
   * отправки — правка ушла бы «поверх того, что там сейчас», то есть защита от чужой правки стала
   * бы её видимостью, а сравнение объявляло бы изменённым то, чего человек не трогал.
   *
   * ⚠️ Заморозить версию через `key={booking.version}` нельзя — выйдет наоборот: чужая правка меняет
   * версию, React пересоздаёт окно, «замороженная» версия обновляется вместе с ним, а набранный
   * текст молча заменяется чужим.
   *
   * ⚠️ `blockNotes` — ПРИМЕЧАНИЕ, А НЕ ПРИЧИНА: `blockReasonLabel` приходит выбором из закрытого
   * набора и на правку не отдаётся, брать из него начальное значение значило бы переписывать
   * причину набранным текстом.
   */
  const [initial] = useState(() => ({
    version: booking.version,
    guestsCount: booking.guest.guestsCount,
    notes: booking.internalNotes ?? '',
    blockNotes: booking.blockNotes ?? '',
  }))

  const [guestsCount, setGuestsCount] = useState(String(initial.guestsCount))
  const [notes, setNotes] = useState(initial.notes)
  const [blockNotes, setBlockNotes] = useState(initial.blockNotes)

  const count = Number(guestsCount)
  const countValid = Number.isInteger(count) && count >= 1
  const notesChanged = notes !== initial.notes
  const blockNotesChanged = blockNotes !== initial.blockNotes
  const countChanged = !blocked && countValid && count !== initial.guestsCount
  const changed = notesChanged || (blocked ? blockNotesChanged : countChanged)

  /*
   * Предел домена — 5000 ЗНАКОВ, и считает он символы: `[...text].length`, а не `text.length`
   * (суррогатная пара — один символ, а `.length` даст два). Обрезаем по краям, как нормализация
   * домена, иначе хвост пробелов «съедал» бы бюджет, которого не занимает.
   *
   * ⚠️ НЕ `maxLength` НА ПОЛЕ: тот молча отрезал бы хвост вставленного текста. Показываем счёт и
   * гасим сохранение — то же правило, что у названия секрета: отказ надо ПОКАЗАТЬ.
   */
  const overflow = (text: string) => Math.max(0, [...text.trim()].length - NOTES_LIMIT)
  const notesOver = overflow(notes)
  const blockNotesOver = blocked ? overflow(blockNotes) : 0
  const tooLong = notesOver > 0 || blockNotesOver > 0

  const conflict = isProblem(update.error) && update.error.status === 409

  const save = () => {
    const body: UpdateBooking = {}
    if (notesChanged) body.internalNotes = notes
    if (blocked) {
      if (blockNotesChanged) body.blockNotes = blockNotes
    } else if (countChanged) {
      body.guestsCount = count
    }

    update.mutate({ body, version: initial.version }, { onSuccess: () => onOpenChange(false) })
  }

  return (
    <Dialog
      open={open}
      /*
       * ⚠️ ПОКА ЗАПРОС ЛЕТИТ, ОКНО НЕ ЗАКРЫВАЕТСЯ: отказ показывает оно само, рядом с набранным
       * (`meta.handledInPlace`), а закрытое отдало бы его голому тосту. Закрывать нельзя ничем: ни
       * «Отменой», ни крестиком, ни Escape, ни кликом по скриму — последние три идут в этот же
       * обработчик. Унести окно может только навигация — «назад», смена брони, уход со
       * страницы, — и отказ тогда скажет общий тост: молчит он, лишь пока экран на месте
       * (`app/providers/query-client.ts`).
       */
      onOpenChange={(next) => {
        if (!next && update.isPending) return
        onOpenChange(next)
      }}
      icon="edit"
      title={blocked ? 'Изменить закрытый период' : 'Изменить бронь'}
      description={
        blocked
          ? 'Здесь правится примечание. Даты и квартира закрытого периода не меняются.'
          : 'Здесь правится число гостей и примечание. Даты, суммы и квартиру бронь менять не умеет — статус меняется действиями карточки.'
      }
      width={520}
    >
      <div className={s.body}>
        {!blocked && (
          <label className={s.field}>
            <span className={s.label}>Сколько гостей</span>
            <Input
              type="number"
              min={1}
              value={guestsCount}
              onChange={(event) => setGuestsCount(event.target.value)}
            />
            {/* Предел доменный: «не меньше единицы». Говорим о нём до отправки, а не после отказа. */}
            {!countValid && <span className={s.hint}>Гостей не может быть меньше одного</span>}
          </label>
        )}

        {blocked && (
          <label className={s.field}>
            <span className={s.label}>Примечание к закрытому периоду</span>
            <Textarea
              rows={2}
              value={blockNotes}
              onChange={(event) => setBlockNotes(event.target.value)}
            />
            {/* Причину показываем рядом, но не даём править: у домена это выбор из закрытого
                набора, и на правку он его не отдаёт. Без этой строки поле выглядело бы так, будто
                менять можно и её. */}
            {booking.blockReasonLabel && (
              <span className={s.hint}>Чем закрыт: {booking.blockReasonLabel} — это не меняется</span>
            )}
            {blockNotesOver > 0 && (
              <span className={s.hint}>Длиннее предела на {blockNotesOver} — уберите лишнее</span>
            )}
          </label>
        )}

        <label className={s.field}>
          <span className={s.label}>Примечание к брони</span>
          <Textarea rows={4} value={notes} onChange={(event) => setNotes(event.target.value)} />
          {/*
           * ⚠️ Это НЕ ПДн гостя — примечание видно всем, у кого есть доступ к брони. Заметки о
           * госте живут в его профиле и приходят только с отдельным правом. Говорим об этом прямо
           * здесь, потому что именно здесь человек решает, что сюда писать.
           */}
          <span className={s.hint}>
            Видно всем, у кого есть доступ к брони. Личные данные гостя сюда писать не нужно — для
            них есть профиль гостя.
          </span>
          {/* Про предел говорим, только когда он превышен: счётчик «12 из 5000» под каждым
              примечанием — шум, до которого доходит один автор из тысячи. */}
          {notesOver > 0 && (
            <span className={s.hint}>
              Длиннее предела в {NOTES_LIMIT} знаков на {notesOver} — уберите лишнее
            </span>
          )}
        </label>

        {/*
         * ⚠️ 409 — НЕ «НЕ УДАЛОСЬ СОХРАНИТЬ». Это «пока вы правили, бронь изменил кто-то ещё», и
         * человеку нужно другое действие: не повторить, а перечитать. Общий тост сказал бы про
         * неудачу и был бы прав ровно наполовину, поэтому окно разбирает этот случай само.
         */}
        {update.isError && (
          <p className={s.error} role="alert">
            {conflict
              ? (problemDetail(update.error) ??
                'Пока вы правили, бронь изменил кто-то ещё. Закройте окно и откройте бронь заново.')
              : problemText(update.error, 'Не удалось сохранить правку')}
          </p>
        )}
      </div>

      <div className={s.footer}>
        {/* Гаснет вместе с прочими путями закрытия: пока запрос летит, показать его отказ РЯДОМ С
            НАБРАННЫМ может только это окно — см. разбор у `onOpenChange`. */}
        <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={update.isPending}>
          Отмена
        </Button>
        <Button
          onClick={save}
          disabled={!changed || tooLong || (!blocked && !countValid) || update.isPending}
        >
          {update.isPending ? 'Сохраняем…' : 'Сохранить'}
        </Button>
      </div>
    </Dialog>
  )
}
